import { memo, type FC } from 'react';

import { Button } from '../../../../Controls/Button/index.tsx';
import Icons from '../../../../Elements/Icons/index.ts';
import type { PaginationControlsProps } from './types.ts';

/**
 * The prev/next page buttons shared by `Table.Info` and `Table.Pagination`. Next is disabled on the
 * last page and when `total === 0` (the latter matters while loading with no data, where the caller
 * still renders the controls). The bound uses `>=` (not `===`) so an out-of-range page — e.g. after
 * `total` shrinks, which usePageInfo clamps for display — keeps Next disabled instead of pushing the
 * page further out of range.
 */
export const PaginationControls: FC<PaginationControlsProps> = memo(function PaginationControls({
  page,
  perPage,
  total,
  onPrev,
  onNext,
  prevDataTestId = 'backward-button',
  nextDataTestId = 'forward-button',
}) {
  const isLastPage = total === 0 || page >= Math.ceil(total / perPage) - 1;

  return (
    <>
      <Button secondary iconOnly onClick={onPrev} disabled={page === 0} dataTestId={prevDataTestId}>
        <Icons.Arrow align='left' />
      </Button>
      <Button secondary iconOnly onClick={onNext} disabled={isLastPage} dataTestId={nextDataTestId}>
        <Icons.Arrow align='right' />
      </Button>
    </>
  );
});
